import jsPDF from 'jspdf'
import { getCompanyInfo } from '../../../shared/CommonFunctions'

const formatDob = (dobValue) => {
  if (!dobValue) return ''
  const d = new Date(dobValue)
  if (Number.isNaN(d.getTime())) return ''
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const yyyy = d.getFullYear()
  return `${dd}-${mm}-${yyyy}`
}

const loadLogoImage = (logoPath) => {
  return new Promise((resolve) => {
    if (!logoPath) return resolve(null)
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => resolve(null)
    img.src = logoPath.startsWith('http') ? logoPath : `/assets/img/${logoPath}`
  })
}

export async function generateFinanceRegisterPdf(financeRegisterData, fileTitles = []) {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  const pageWidth = doc.internal.pageSize.getWidth()
  const marginX = 10
  let y = 12

  const companyInfo = await getCompanyInfo()
  const companyName = companyInfo?.CompanyName || 'Company'
  const companyAddress = companyInfo?.Address || ''
  const companyEmail = companyInfo?.EmailId || ''
  const logoPath = companyInfo?.LogoImagePath || ''
  const logoImage = await loadLogoImage(logoPath)

  if (logoImage) {
    const logoWidth = 40
    const logoHeight = 24
    doc.addImage(logoImage, 'PNG', marginX, y - 10, logoWidth, logoHeight)
  }

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(16)
  doc.text(companyName, pageWidth / 2, y, { align: 'center' })
  y += 6
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)
  if (companyAddress) {
    doc.text(companyAddress, pageWidth / 2, y, { align: 'center' })
    y += 4
  }
  if (companyEmail) {
    doc.text(companyEmail, pageWidth / 2, y, { align: 'center' })
    y += 4
  }

  y += 4
  doc.rect(marginX, y, pageWidth - marginX * 2, 82)
  y += 6

  const lineLeft = marginX + 28
  const lineRight = pageWidth - marginX - 5
  const formatValue = (value) => (value === null || value === undefined ? '' : String(value).toUpperCase())

  const writeValue = (value, x, yPos) => {
    const output = formatValue(value)
    if (!output) return
    doc.setTextColor(0, 102, 204)
    doc.text(output, x, yPos)
    doc.setTextColor(0, 0, 0)
  }

  const writeLine = (label, value, x, yPos, lineWidth = 60) => {
    doc.text(label, x, yPos)
    const labelWidth = doc.getTextWidth(label)
    const lineStartX = x + labelWidth + 2
    doc.line(lineStartX, yPos + 1, lineStartX + lineWidth, yPos + 1)
    writeValue(value, lineStartX, yPos - 1)
  }

  const residenceAddress = [
    financeRegisterData?.CustomerAddressLine1,
    financeRegisterData?.CustomerAddressLine2
  ].filter(Boolean).join(', ')

  const residenceCityState = [
    financeRegisterData?.CustomerCity,
    financeRegisterData?.CustomerState
  ].filter(Boolean).join(', ')

  const officeAddress = [
    financeRegisterData?.OfficeAddressLine1,
    financeRegisterData?.OfficeAddressLine2
  ].filter(Boolean).join(', ')

  const officeCityState = [
    financeRegisterData?.OfficeCity,
    financeRegisterData?.OfficeState
  ].filter(Boolean).join(', ')

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(9)

  writeLine('APPLICATION NAME:', financeRegisterData?.CustomerName || '', marginX + 2, y, 150)
  y += 8
  const famName = financeRegisterData?.FamilyMemberName || ''
  const famMobile = financeRegisterData?.FamilyMemberMobileNumber || ''
  const famDisplay = famName && famMobile ? `${famName} (${famMobile})` : famName || famMobile
  writeLine('FAMILY MEMBER NAME (NUMBER):', famDisplay || '', marginX + 2, y, 129)
  y += 8

  doc.text('RESIDENCE:', marginX + 2, y)
  const residenceLineWidth = 65
  doc.line(lineLeft, y + 8, lineLeft + residenceLineWidth, y + 8)
  const residenceText = formatValue(residenceAddress || '')
  if (residenceText) {
    const residenceLines = doc.splitTextToSize(residenceText, residenceLineWidth)
    doc.setTextColor(0, 102, 204)
    doc.text(residenceLines, lineLeft, y - 1)
    doc.setTextColor(0, 0, 0)
  }
  doc.text('OFFICE ADD:', marginX + 98, y)
  const officeLineWidth = 65
  doc.line(marginX + 120, y + 8, lineRight, y + 8)
  const officeText = formatValue(officeAddress || '')
  if (officeText) {
    const officeLines = doc.splitTextToSize(officeText, officeLineWidth)
    doc.setTextColor(0, 102, 204)
    doc.text(officeLines, marginX + 120, y - 1)
    doc.setTextColor(0, 0, 0)
  }
  y += 15

  writeLine('LANDMARK:', financeRegisterData?.CustomerAddressLine3 || '', marginX + 2, y, 70)
  writeLine('LANDMARK:', financeRegisterData?.OfficeAddressLine3 || '', marginX + 98, y, 67)
  y += 8
  writeLine('PIN CODE:', financeRegisterData?.CustomerPinNumber || '', marginX + 2, y, 72)
  writeLine('PIN CODE:', financeRegisterData?.OfficePinNumber || '', marginX + 98, y, 69)
  y += 8
  writeLine('MOBILE NUMBER:', financeRegisterData?.MobileNo || '', marginX + 2, y, 61)
  writeLine('MOBILE NUMBER:', financeRegisterData?.MobileNo || '', marginX + 98, y, 58)
  y += 8
  writeLine('CITY/STATE:', residenceCityState || '', marginX + 2, y, 70)
  writeLine('CITY/STATE:', officeCityState || '', marginX + 98, y, 67)
  y += 8
  writeLine('INCOME:', financeRegisterData?.AnualIncome || '', marginX + 2, y, 70)
  y += 8
  writeLine('DOB:', formatDob(financeRegisterData?.DOB) || '', marginX + 2, y, 80)

  y = 106
  doc.rect(marginX, y, pageWidth - marginX * 2, 14)
  y += 7
  doc.text('IDENTITY & RESIDECIAL PROOF :', marginX + 4, y)
  if (fileTitles.length) {
    y += 5
    writeValue(fileTitles.join(', '), marginX + 4, y)
  }

  y = 122
  doc.rect(marginX, y, pageWidth - marginX * 2, 100)
  y += 7
  writeLine('LOGIN TERM & IDENTITY PROOF:', '', marginX + 2, y, 0)
  writeLine('ROI:', financeRegisterData?.InterestRate || '', marginX + 98, y, 78)
  y += 8
  writeLine('TENURE:', financeRegisterData?.EMITenure || '', marginX + 98, y, 71)
  y += 8
  writeLine('LOAN AMOUNT:', financeRegisterData?.VehicleCost || '', marginX + 98, y, 60)
  y += 8
  writeLine('VEH BRAND:', '', marginX + 98, y, 66)
  y += 8
  writeLine('MODEL:', financeRegisterData?.VehicleLabel || '', marginX + 98, y, 72)
  y += 8
  writeLine('HP:', '', marginX + 98, y, 79)
  y += 8
  writeLine('EMAIL:', financeRegisterData?.EmailId || '', marginX + 98, y, 73)
  y += 8
  writeLine('PROFILE:', '', marginX + 98, y, 70)

  y = 198
  doc.text('REFERENCE DETAIL:', marginX + 2, y)
  y += 8
  writeLine('REF 1 NAME:', financeRegisterData?.Reference1Name || '', marginX + 2, y, 70)
  writeLine('REF 2 NAME:', financeRegisterData?.Reference2Name || '', marginX + 98, y, 67)
  y += 8
  writeLine('MOB:', financeRegisterData?.Reference1MobileNumber || '', marginX + 2, y, 82)
  writeLine('MOB:', financeRegisterData?.Reference2MobileNumber || '', marginX + 98, y, 79)

  const dataUri = doc.output('datauristring')
  const base64 = dataUri.split(',')[1]
  return { dataUri, base64 }
}